/**
 * Hook for getting data to populate dynamic controls
 */

import { LabelValue, LabelValueInactive, MongoIdSqlId } from "../Types/DtoTypes";

import { ListDataTypes } from "../Constants/ListDataTypes";
import { DynamicFacepileItem } from "../Types/ControlTypes";
import { Person, City } from "../Models/PowerSyncModels/models";
import { GeneralDynamicConfig } from "../Constants/General";
import { PERSON_STATUSES } from "../Constants/AppConstants";
import moment from "moment";
import { usePowerSyncData } from "../Providers/PowerSyncDataProvider";
import { usePowerSyncUser } from "../Providers/PowerSyncUserProvider";

type DynamicDataHook = {
    getData(
        tableName: string,
        selectedValue?: string,
        multiselect?: boolean,
        incidentType?: string,
        filter?: string,
    ): Promise<LabelValue<string>[]>;

    getMultiselectionData(
        tableName: string,
        selectedValue?: string,
        orgId?: string,
    ): Promise<DynamicFacepileItem[]>;
};

const useDynamicData = (): DynamicDataHook => {
    const {
        projects,
        people,
        SQLUsers,
        animalTypes,
        certificationTypes,
        classificationUnits,
        crews,
        departments,
        dispositions,
        equipments,
        equipmentTypes,
        equipmentVehicleProfiles,
        equipmentHeavyProfiles,
        equipmentOtherProfiles,
        hazardTypes,
        incidentProbabilities,
        incidentTypes,
        incidentClassifications,
        jobs,
        locations,
        locationTypes,
        mechanisms,
        organisations,
        orgRelations,
        peopleTypes,
        roles,
        severityLevels,
        shifts,
        tasks,
        taskTypes,
        trainingTypes,
        OIICS_BodyParts,
        OIICS_Natures,
        documentTypes,
        cities,
        countries,
        provStates,
    } = usePowerSyncData();

    const user = usePowerSyncUser();

    function sortOptions(options: LabelValue<string>[]): LabelValue<string>[] {
        return options.sort((a, b) =>
            a && b && a!.label!.toLowerCase() > b!.label!.toLowerCase()
                ? 1
                : a && b && a!.label!.toLowerCase() < b!.label!.toLowerCase()
                  ? -1
                  : 0,
        );
    }

    function sortOptionsWithStatus(
        options: LabelValueInactive<string>[],
    ): LabelValueInactive<string>[] {
        return options.sort((a, b) =>
            a &&
            b &&
            a!.label!.toLowerCase() > b!.label!.toLowerCase() &&
            !(!a.inActive && b.inActive)
                ? 1
                : a &&
                    b &&
                    a!.label!.toLowerCase() < b!.label!.toLowerCase() &&
                    !(a.inActive && !b.inActive)
                  ? -1
                  : !a.inActive && b.inActive
                    ? -1
                    : a.inActive && !b.inActive
                      ? 1
                      : 0,
        );
    }

    function sortFacePile(options: DynamicFacepileItem[]): DynamicFacepileItem[] {
        return options.sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase()
                ? 1
                : a.label.toLowerCase() < b.label.toLowerCase()
                  ? -1
                  : 0,
        );
    }

    //Data for multiselection components
    const getMultiselectionData = async (tableName: string, selectedValue: string) => {
        let newOptions: DynamicFacepileItem[] = [];
        let selectedValues: string[] = [];

        if (selectedValue) {
            let parsedArray = JSON.parse(selectedValue!) as string[];
            let parsedValues = parsedArray.map((x) => (JSON.parse(x) as MongoIdSqlId).mongoId!);
            selectedValues = parsedValues;
        }

        if (tableName === ListDataTypes.CLASSIFICATIONUNITS) {
            let cuArray = classificationUnits;

            newOptions = cuArray.map((p) => ({
                label: p.name,
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name,
                    SQLServerId: p.SQLServerId,
                }),
                avatar: "",
            }));

            newOptions = sortFacePile(newOptions);
        } else if (tableName === ListDataTypes.PEOPLE) {
            let peopleArray = people;

            peopleArray = peopleArray.filter((x) => !x.isDeleted || selectedValues.includes(x._id));

            newOptions = peopleArray.map((p) => ({
                label:
                    p.firstName +
                    " " +
                    p.lastName +
                    (p.inactiveDate && p.inactiveDate <= moment().startOf("day").toDate()
                        ? " - Inactive"
                        : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label:
                        p.firstName +
                        " " +
                        p.lastName +
                        (p.inactiveDate && p.inactiveDate <= moment().startOf("day").toDate()
                            ? " - Inactive"
                            : ""),
                    SQLServerId: p.SQLServerId,
                }),
                avatar: p.photoThumbnail ?? "",
            }));

            newOptions = sortFacePile(newOptions);
        } else if (tableName === ListDataTypes.PROJECTS) {
            let projectArray = projects;

            projectArray = projectArray.filter(
                (x) => !x.isDeleted || selectedValues.includes(x._id),
            );

            newOptions = projectArray.map((p) => ({
                label:
                    p.name +
                    ((p.activeStartDate && p.activeStartDate >= moment().startOf("day").toDate()) ||
                    (p.activeEndDate && p.activeEndDate <= moment().startOf("day").toDate())
                        ? " - Inactive"
                        : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name,
                    SQLServerId: p.SQLServerId,
                }),
                avatar: "",
            }));
            newOptions = sortFacePile(newOptions);
        }

        return newOptions;
    };

    //Data for populating dropdowns/ autocomplete that show a label and have a string value
    const getData = async (
        tableName: string,
        selectedValue: string,
        multiselect: boolean,
        incidentType: string,
        filter?: string,
    ): Promise<LabelValue<string>[]> => {
        let newOptions: LabelValue<string>[] = [];
        let selectedValues: string[] = [];

        if (selectedValue) {
            try {
                if (multiselect) {
                    let parsedArray = JSON.parse(selectedValue!) as Array<string>;
                    let parsedValues = parsedArray.map(
                        (x) => (JSON.parse(x) as MongoIdSqlId).mongoId!,
                    );
                    selectedValues = parsedValues;
                } else {
                    let parsedValues = JSON.parse(selectedValue!) as MongoIdSqlId;
                    selectedValues = [parsedValues.mongoId!];
                }
            } catch (error) {
                console.log("Error: Bad Control value serialization");
            }
        }

        if (tableName === ListDataTypes.PEOPLE) {
            let peopleArray = people;

            peopleArray = peopleArray.filter((x) => !x.isDeleted || selectedValues.includes(x._id));
            newOptions = peopleArray.map((p) => ({
                label:
                    p.firstName +
                    " " +
                    p.lastName +
                    (p.inactiveDate && p.inactiveDate <= moment().startOf("day").toDate()
                        ? " - Inactive"
                        : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label:
                        p.firstName +
                        " " +
                        p.lastName +
                        (p.inactiveDate && p.inactiveDate <= moment().startOf("day").toDate()
                            ? " - Inactive"
                            : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.SQLUser) {
            let usersArray = SQLUsers;
            usersArray = usersArray.filter(
                (x) =>
                    (!x.isDeleted && x.isActive && x.statusObj!.name == PERSON_STATUSES.ACCESS) ||
                    selectedValues.includes(x._id),
            );
            newOptions = usersArray.map((p) => ({
                label: p.firstName + " " + p.lastName + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.firstName + " " + p.lastName + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.PROJECTS) {
            let projectsArray = projects;
            projectsArray = projectsArray.filter(
                (x) => !x.isDeleted || selectedValues.includes(x._id),
            );
            newOptions = projectsArray.map((p) => ({
                label:
                    p.name +
                    ((p.activeStartDate && p.activeStartDate >= moment().startOf("day").toDate()) ||
                    (p.activeEndDate && p.activeEndDate <= moment().startOf("day").toDate())
                        ? " - Inactive"
                        : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name,
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.ANIMALTYPES) {
            let animalTypesArray = animalTypes;
            animalTypesArray = animalTypesArray.filter(
                (x) => !x.isDeleted || selectedValues.includes(x._id),
            );
            newOptions = animalTypesArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.CERTIFICATIONTYPES) {
            let certsArray = certificationTypes;
            certsArray = certsArray.filter((x) => !x.isDeleted || selectedValues.includes(x._id));
            newOptions = certsArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.CLASSIFICATIONUNITS) {
            let cuArray = classificationUnits;
            cuArray = cuArray.filter((x) => selectedValues.includes(x._id));
            newOptions = cuArray.map((p) => ({
                label: p.name,
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name,
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.CREWS) {
            let crewsArray = crews;
            crewsArray = crewsArray.filter((x) => !x.isDeleted || selectedValues.includes(x._id));
            newOptions = crewsArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.JOBS) {
            let jobsArray = await jobs;
            jobsArray = jobsArray.filter((x) => !x.isDeleted || selectedValues.includes(x._id));
            newOptions = jobsArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.DEPARTMENTS) {
            let departmentsArray = departments;
            departmentsArray = departmentsArray.filter(
                (x) => !x.isDeleted || selectedValues.includes(x._id),
            );
            newOptions = departmentsArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.EQUIPMENTVEHICLEPROFILES) {
            let array = equipmentVehicleProfiles;

            newOptions = array.map((a) => ({
                label: a.name + " - " + a.number + getEquipmentStatus(a.status),
                inActive: getEquipmentStatus(a.status) != "",
                value: JSON.stringify({
                    mongoId: a._id,
                    label: a.name + " - " + a.number + getEquipmentStatus(a.status),
                    SQLServerId: a.SQLServerId,
                }),
            }));
            newOptions = sortOptionsWithStatus(newOptions);
        } else if (tableName === ListDataTypes.EQUIPMENTHEAVYPROFILES) {
            let array = equipmentHeavyProfiles;

            newOptions = array.map((a) => ({
                label: a.name + " - " + a.number + getEquipmentStatus(a.status),
                inActive: getEquipmentStatus(a.status) != "",
                value: JSON.stringify({
                    mongoId: a._id,
                    label: a.name + " - " + a.number + getEquipmentStatus(a.status),
                    SQLServerId: a.SQLServerId,
                }),
            }));

            newOptions = sortOptionsWithStatus(newOptions);
        } else if (tableName === ListDataTypes.EQUIPMENTOTHERPROFILES) {
            let array = equipmentOtherProfiles;

            newOptions = array.map((a) => ({
                label: a.name + " - " + a.number + getEquipmentStatus(a.status),
                inActive: getEquipmentStatus(a.status) != "",
                value: JSON.stringify({
                    mongoId: a._id,
                    label: a.name + " - " + a.number + getEquipmentStatus(a.status),
                    SQLServerId: a.SQLServerId,
                }),
            }));

            newOptions = sortOptionsWithStatus(newOptions);
        } else if (tableName === ListDataTypes.HAZARDTYPES) {
            let hazardsArray = hazardTypes;
            hazardsArray = hazardsArray.filter(
                (x) => !x.isDeleted || selectedValues.includes(x._id),
            );
            newOptions = hazardsArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.TASKS) {
            let tasksArray = tasks;
            if (filter) {
                try {
                    let valObj = JSON.parse(filter) as MongoIdSqlId;
                    tasksArray = tasksArray.filter(
                        (x) => x.taskTypeSQLServerId === valObj.SQLServerId,
                    );
                } catch (error) {}
            }

            newOptions = tasksArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.TRAININGTYPES) {
            let trainTypesArray = trainingTypes;
            trainTypesArray = trainTypesArray.filter(
                (x) => !x.isDeleted || selectedValues.includes(x._id),
            );
            newOptions = trainTypesArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.EQUIPMENTTYPES) {
            let equipmentTypesArray = equipmentTypes;
            newOptions = equipmentTypesArray.map((p) => ({
                label: p.name,
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name,
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.EQUIPMENTS) {
            let equipmentArray = equipments;
            newOptions = equipmentArray.map((p) => ({
                label: p.name,
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name,
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.ROLES) {
            let rolesArray = roles;
            newOptions = rolesArray.map((p) => ({
                label: p.name,
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name,
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.SHIFTS) {
            let shiftsArray = shifts;
            shiftsArray = shiftsArray.filter((x) => !x.isDeleted || selectedValues.includes(x._id));
            newOptions = shiftsArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.SEVERITYLEVELS) {
            let sevLevelsArray = severityLevels;
            sevLevelsArray = sevLevelsArray.filter(
                (x) => !x.isDeleted || selectedValues.includes(x._id),
            );
            newOptions = sevLevelsArray.map((p) => ({
                label: p.name + " " + p.value + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + " " + p.value + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.MECHANISMS) {
            let mechArray = mechanisms;
            mechArray = mechArray.filter((x) => !x.isDeleted || selectedValues.includes(x._id));
            newOptions = mechArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.DISPOSTIONS) {
            let disArray = dispositions;
            disArray = disArray.filter((x) => !x.isDeleted || selectedValues.includes(x._id));
            newOptions = disArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.TASKTYPES) {
            let taskTypeArray = taskTypes;
            taskTypeArray = taskTypeArray.filter(
                (x) => !x.isDeleted || selectedValues.includes(x._id),
            );
            newOptions = taskTypeArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.INCIDENTPROBABILITIES) {
            let incProbsArray = incidentProbabilities;
            incProbsArray = incProbsArray.filter(
                (x) => !x.isDeleted || selectedValues.includes(x._id),
            );
            newOptions = incProbsArray.map((p) => ({
                label: p.name + " " + p.value + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + " " + p.value + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.INCIDENTCLASSIFICATIONS) {
            let incidentClasses = incidentClassifications;
            incidentClasses = incidentClasses.filter(
                (x) => !x.isDeleted || selectedValues.includes(x._id),
            );
            if (incidentType) {
                let filteredTypes = incidentTypes
                    .filter((x) => x.name.toLowerCase() == incidentType.toLowerCase())
                    .map((x) => x.SQLServerId);

                let filteredClasses = incidentClasses.filter(
                    (x) =>
                        x.incidentTypeSQLServerId &&
                        filteredTypes.includes(x.incidentTypeSQLServerId),
                );
                newOptions = filteredClasses.map((p) => ({
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    value: JSON.stringify({
                        mongoId: p._id,
                        label: p.name + (!p.isActive ? " - Inactive" : ""),
                        SQLServerId: p.SQLServerId,
                    }),
                }));
            } else {
                newOptions = incidentClasses.map((p) => ({
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    value: JSON.stringify({
                        mongoId: p._id,
                        label: p.name + (!p.isActive ? " - Inactive" : ""),
                        SQLServerId: p.SQLServerId,
                    }),
                }));
            }
        } else if (tableName === ListDataTypes.INCIDENTTYPES) {
            let incTypesArray = incidentTypes;
            incTypesArray = incTypesArray.filter(
                (x) => !x.isDeleted || selectedValues.includes(x._id),
            );
            newOptions = incTypesArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.LOCATIONS) {
            let locArray = locations;
            locArray = locArray.filter((x) => !x.isDeleted || selectedValues.includes(x._id));
            newOptions = locArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.LOCATIONTYPES) {
            let locTypeArray = locationTypes;
            locTypeArray = locTypeArray.filter(
                (x) => !x.isDeleted || selectedValues.includes(x._id),
            );
            newOptions = locTypeArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.ORGANISATIONS) {
            let orgId = user.getUserInfo().orgId.toString();
            let relatedOrgs = orgRelations
                .filter((r) => r.childOrgId === orgId)
                .map((r) => r.parentOrgId.toString());

            let orgArray = organisations;
            orgArray = orgArray.filter(
                (x) =>
                    (!x.isDeleted || selectedValues.includes(x._id)) &&
                    relatedOrgs.includes(x.SQLServerId.toString()),
            );
            newOptions = orgArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.NATURES) {
            let natArray = OIICS_Natures;
            newOptions = natArray.map((p) => ({
                label: p.name,
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name,
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.BODYPARTS) {
            let bpArray = OIICS_BodyParts;
            newOptions = bpArray.map((p) => ({
                label: p.name,
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name,
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.TRAININGTYPES) {
            let ttArray = trainingTypes;
            newOptions = ttArray.map((t) => ({
                label: t.name,
                value: JSON.stringify({
                    mongoId: t._id,
                    label: t.name,
                    SQLServerId: t.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.CERTIFICATIONTYPES) {
            let ctArray = certificationTypes;
            newOptions = ctArray.map((c) => ({
                label: c.name,
                value: JSON.stringify({
                    mongoId: c._id,
                    label: c.name,
                    SQLServerId: c.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.PEOPLETYPES) {
            let peopleTypeArray = peopleTypes;
            peopleTypeArray = peopleTypeArray.filter(
                (x) => !x.isDeleted || selectedValues.includes(x._id),
            );
            newOptions = peopleTypeArray.map((p) => ({
                label: p.name + (!p.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: p._id,
                    label: p.name + (!p.isActive ? " - Inactive" : ""),
                    SQLServerId: p.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.DOCUMENTTYPES) {
            let dtArray = documentTypes;

            //Sort by isActive, then by name
            dtArray = dtArray.sort(
                (a, b) =>
                    Number(b.isActive) - Number(a.isActive) ||
                    a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
            );

            newOptions = dtArray.map((t) => ({
                label: t.name + (!t.isActive ? " - Inactive" : ""),
                value: JSON.stringify({
                    mongoId: t._id,
                    label: t.name + (!t.isActive ? " - Inactive" : ""),
                    SQLServerId: t.SQLServerId,
                }),
            }));
        } else if (tableName === ListDataTypes.CITIES) {
            let citiesArray: City[] = [];
            if (filter) {
                try {
                    let valObj = JSON.parse(filter) as MongoIdSqlId;
                    citiesArray = cities.filter(
                        (x) =>
                            x.provStateSQLServerId?.toLowerCase() ==
                            valObj.SQLServerId?.toLowerCase(),
                    );
                } catch (error) {}
            } else {
                citiesArray = cities;
            }

            newOptions = citiesArray.map((t) => ({
                label: t.cityName ?? "",
                value: JSON.stringify({
                    mongoId: t._id,
                    label: t.cityName ?? "",
                    SQLServerId: t.SQLServerId ?? "",
                }),
            }));
        } else if (tableName === ListDataTypes.PROVSTATES) {
            let retVal = provStates;

            if (filter) {
                try {
                    let valObj = JSON.parse(filter) as MongoIdSqlId;
                    retVal = provStates.filter(
                        (x) =>
                            x.countrySQLServerId?.toLowerCase() ==
                            valObj.SQLServerId?.toLowerCase(),
                    );
                } catch (error) {}
            }

            newOptions = retVal.map((t) => ({
                label: t.provState ?? "",
                value: JSON.stringify({
                    mongoId: t._id,
                    label: t.provState ?? "",
                    SQLServerId: t.SQLServerId ?? "",
                }),
            }));
        } else if (tableName === ListDataTypes.COUNTRIES) {
            newOptions = countries.map((c) => ({
                label: c.countryName ?? "",
                value: JSON.stringify({
                    mongoId: c._id,
                    label: c.countryName ?? "",
                    SQLServerId: c.SQLServerId,
                    so: c.sortOrder,
                }),
            }));
        }

        newOptions = sortOptions(newOptions);

        return newOptions;
    };

    function getEquipmentStatus(status: string): string {
        let result = "";

        let statusObj = GeneralDynamicConfig.SEGMENT_STATUS.segments.find((x) => x.value == status);
        if (statusObj && statusObj.value != "Active") result = " - " + statusObj.name;

        return result;
    }

    return { getData: getData, getMultiselectionData: getMultiselectionData };
};

export default useDynamicData;
